// frontend/src/pages/admin/AdminGrievancesPage.js
//
// Super admin: members' grievances (migration_048). Anonymous ones never show
// a name. Reply, and set the status (Open / In progress / Resolved / Closed).
//
//   /admin/grievances       list (open first, unread marked)
//   /admin/grievances/:id   one grievance with its thread

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../utils/api';

const NAVY = '#1E3A6E';
const STATUS = {
  open:        { label: 'Open',        bg: '#fef3c7', fg: '#92400e' },
  in_progress: { label: 'In progress', bg: '#dbeafe', fg: '#1d4ed8' },
  resolved:    { label: 'Resolved',    bg: '#dcfce7', fg: '#166534' },
  closed:      { label: 'Closed',      bg: '#f1f5f9', fg: '#475569' },
};
const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const who = (g) => g.is_anonymous ? '🕶️ Anonymous member' : `🙋 ${g.member_name || 'Member'}${g.ucc_id ? ` (${g.ucc_id})` : ''}`;

function Header({ title, sub, back }) {
  return (
    <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '16px 16px 14px' }}>
      {back && <Link to={back} style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>← All grievances</Link>}
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: back ? 6 : 0 }}>Admin · Grievances</div>
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.3rem', color: NAVY }}>{title}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function List() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('active');
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get('/grievances/admin').then(r => setData(r.data)).catch(e => setErr(e.response?.data?.error || 'Could not load'));
  }, []);
  if (err) return <div><Header title="📮 Grievances" /><div style={{ padding: 16, color: '#dc2626' }}>{err}</div></div>;
  if (!data) return <div className="loading-spinner"><div className="spinner" /></div>;
  const list = data.grievances.filter(g => filter === 'all' || (filter === 'active' ? ['open', 'in_progress'].includes(g.status) : ['resolved', 'closed'].includes(g.status)));
  const chip = (on) => ({ padding: '6px 12px', borderRadius: 999, border: `1px solid ${on ? NAVY : '#e5e7eb'}`, background: on ? NAVY : 'white',
    color: on ? 'white' : '#374151', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' });
  return (
    <div style={{ paddingBottom: 20 }}>
      <Header title="📮 Grievances" sub="Raised by members. Only you can see these; anonymous ones never show a name." />
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <button style={chip(filter === 'active')} onClick={() => setFilter('active')}>Open & in progress</button>
          <button style={chip(filter === 'done')} onClick={() => setFilter('done')}>Resolved & closed</button>
          <button style={chip(filter === 'all')} onClick={() => setFilter('all')}>All</button>
        </div>
        {list.length === 0 && <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Nothing here.</div>}
        {list.map(g => {
          const st = STATUS[g.status] || STATUS.open;
          return (
            <Link key={g.id} to={`/admin/grievances/${g.id}`} style={{ textDecoration: 'none' }}>
              <div style={{ background: 'white', border: `1px solid ${g.unread ? '#93c5fd' : '#e5e7eb'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: NAVY }}>{g.unread && <span style={{ color: '#2563eb' }}>● </span>}{g.subject}</div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    {who(g)} · {data.categories[g.category] || g.category} · {when(g.last_member_at)}{g.messages ? ` · ${g.messages} message${g.messages === 1 ? '' : 's'}` : ''}
                  </div>
                </div>
                <span style={{ background: st.bg, color: st.fg, borderRadius: 6, padding: '2px 8px', fontSize: '0.68rem', fontWeight: 800 }}>{st.label}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Detail({ id }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get('/grievances/admin/' + id).then(r => setData(r.data)).catch(e => setErr(e.response?.data?.error || 'Could not load'));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div><Header title="Grievance" back="/admin/grievances" /><div style={{ padding: 16, color: '#dc2626' }}>{err}</div></div>;
  if (!data) return <div className="loading-spinner"><div className="spinner" /></div>;
  const g = data.grievance;

  async function send(status) {
    if (!reply.trim() && !status) return;
    setBusy(true);
    try { await api.post(`/grievances/admin/${g.id}/reply`, { body: reply.trim(), status }); setReply(''); load(); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not send'); }
    finally { setBusy(false); }
  }
  async function setStatus(status) {
    setBusy(true);
    try { await api.patch(`/grievances/admin/${g.id}`, { status }); load(); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not change'); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ paddingBottom: 30 }}>
      <Header back="/admin/grievances" title={g.subject}
        sub={`${who(g)} · ${data.categories[g.category] || g.category} · raised ${when(g.created_at)}`} />
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {Object.entries(STATUS).map(([k, v]) => (
            <button key={k} disabled={busy} onClick={() => g.status !== k && setStatus(k)}
              style={{ padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontWeight: 800, fontSize: '0.75rem',
                border: `1.5px solid ${g.status === k ? v.fg : '#e5e7eb'}`, background: g.status === k ? v.bg : 'white', color: g.status === k ? v.fg : '#475569' }}>
              {g.status === k ? '● ' : ''}{v.label}
            </button>
          ))}
        </div>

        <div style={{ fontSize: '0.88rem', color: '#334155', whiteSpace: 'pre-wrap', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>{g.body}</div>
        {g.messages.map(m => (
          <div key={m.id} style={{ marginTop: 8, display: 'flex', justifyContent: m.from_admin ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '85%', background: m.from_admin ? '#eef2ff' : '#f1f5f9', borderRadius: 10, padding: '8px 10px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 800, color: m.from_admin ? NAVY : '#64748b' }}>
                {m.from_admin ? 'You (management)' : (g.is_anonymous ? 'Anonymous member' : g.member_name)} · {when(m.created_at)}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#334155', whiteSpace: 'pre-wrap' }}>{m.body}</div>
            </div>
          </div>
        ))}

        <textarea className="form-control" rows={3} value={reply} onChange={e => setReply(e.target.value)}
          placeholder={g.is_anonymous ? 'Reply — the member sees it on his Grievances page and gets a push (his name stays hidden)' : 'Reply — the member gets a notice and a push'}
          style={{ marginTop: 14, resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button disabled={busy || !reply.trim()} onClick={() => send(null)}
            style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', background: NAVY, color: 'white', fontWeight: 800, fontFamily: 'Rajdhani,sans-serif', cursor: 'pointer', opacity: busy || !reply.trim() ? 0.5 : 1 }}>
            Send reply
          </button>
          <button disabled={busy || !reply.trim()} onClick={() => send('resolved')}
            style={{ flex: 1, padding: 12, borderRadius: 10, border: '1.5px solid #16a34a', background: 'white', color: '#16a34a', fontWeight: 800, fontFamily: 'Rajdhani,sans-serif', cursor: 'pointer', opacity: busy || !reply.trim() ? 0.5 : 1 }}>
            Reply & mark resolved
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminGrievancesPage() {
  const { id } = useParams();
  return id ? <Detail id={id} /> : <List />;
}
